import { Paper, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState, type FC } from "react";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import { fetchStatisticsGeographicInsights } from "../statisticsGeographicInsightsApi";
import {
  buildStatisticsQueryFilters,
  type StatisticsFilters,
} from "../types";
import { StatisticsGeographicComparisonChart } from "./StatisticsGeographicComparisonChart";
import { StatisticsGeographicHeatMap } from "./StatisticsGeographicHeatMap";

interface StatisticsGeographicSectionProps {
  filters: StatisticsFilters;
  /** When false, skips the network request (used to defer behind KPI load). */
  enabled?: boolean;
}

type ComparisonLevel = "district" | "sub_district";

const cardSx = {
  p: { xs: 1.5, md: 2.5 },
  borderRadius: 3,
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "background.paper",
} as const;

export const StatisticsGeographicSection: FC<StatisticsGeographicSectionProps> = ({
  filters,
  enabled = true,
}) => {
  const { currOrganization } = useOrganization();
  const [comparisonLevel, setComparisonLevel] =
    useState<ComparisonLevel>("district");

  const { data, isLoading } = useQuery({
    queryKey: ["statistics-geographic-insights", currOrganization?.id, filters],
    queryFn: () =>
      fetchStatisticsGeographicInsights(
        buildStatisticsQueryFilters(currOrganization!.id, filters),
      ),
    enabled: enabled && !!currOrganization?.id,
    staleTime: 1000 * 60 * 2,
  });

  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      <Typography variant="h6" fontWeight={600} color="primary">
        פילוח גיאוגרפי
      </Typography>

      <Paper elevation={0} sx={cardSx}>
        <StatisticsGeographicHeatMap data={data} isLoading={isLoading} />
      </Paper>

      <Paper elevation={0} sx={cardSx}>
        <StatisticsGeographicComparisonChart
          districtComparison={data?.districtComparison ?? []}
          subDistrictComparison={data?.subDistrictComparison ?? []}
          level={comparisonLevel}
          onLevelChange={setComparisonLevel}
          isLoading={isLoading}
        />
      </Paper>
    </Stack>
  );
};
